/**
 * BlogPlaceHub — /blog/places/:slug*
 *
 * Per-place community hub: AI intro (with listen + translate),
 * lightweight news feed and any published articles distributed to
 * this location.
 *
 * Slug format: hierarchical, e.g. `india`, `india/kerala`,
 * `india/kerala/cochin`. Uses catchall `:slug*` route so all depths
 * hit the same page.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin, Newspaper, ArrowRight, Loader2, Volume2, Languages, Globe,
  ChevronRight, Sparkles, Cloud, CloudSun, CloudRain, CloudSnow,
  CloudDrizzle, CloudLightning, Sun, Wind, Camera,
} from "lucide-react";
import BlogHeader from "@/components/blog/BlogHeader";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

// Common translation targets. Leaves EN as the source language.
const LANGS = [
  { code: "en",  label: "English" },
  { code: "hi",  label: "हिन्दी" },
  { code: "ml",  label: "മലയാളം" },
  { code: "ta",  label: "தமிழ்" },
  { code: "bn",  label: "বাংলা" },
  { code: "es",  label: "Español" },
  { code: "fr",  label: "Français" },
];

// Open-Meteo WMO icon name → Lucide icon
const WEATHER_ICON = {
  "sun":              Sun,
  "cloud":            Cloud,
  "cloud-sun":        CloudSun,
  "cloud-rain":       CloudRain,
  "cloud-rain-heavy": CloudRain,
  "cloud-drizzle":    CloudDrizzle,
  "cloud-snow":       CloudSnow,
  "cloud-lightning":  CloudLightning,
};


export default function BlogPlaceHub() {
  const { "*": slugParam } = useParams();
  const slug = (slugParam || "").replace(/^\/+|\/+$/g, "");
  const navigate = useNavigate();

  const [intro, setIntro] = useState(null);
  const [introLoading, setIntroLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [weather, setWeather] = useState(null);
  const [related, setRelated] = useState([]);
  const [lang, setLang] = useState("en");
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);

  const [ttsStatus, setTtsStatus] = useState(null);
  const [ttsBusy, setTtsBusy] = useState(false);

  const breadcrumb = useMemo(() => {
    if (!slug) return [];
    const parts = slug.split("/");
    return parts.map((p, i) => ({
      label: p.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      slug: parts.slice(0, i + 1).join("/"),
    }));
  }, [slug]);

  // Fetch intro + articles + news in parallel
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    setIntroLoading(true);
    setNewsLoading(true);
    setTranslated(null);
    setLang("en");

    (async () => {
      try {
        const r = await fetch(`${BASE}/place/${slug}/intro`);
        if (r.ok) {
          const d = await r.json();
          if (alive) setIntro(d);
        }
      } catch { /* noop */ }
      if (alive) setIntroLoading(false);
    })();

    (async () => {
      try {
        const r = await fetch(`${BASE}/hub/place/${encodeURIComponent(slug)}?limit=8`);
        if (r.ok) {
          const d = await r.json();
          if (alive) setArticles(d.articles || []);
        }
      } catch { /* noop */ }
    })();

    (async () => {
      try {
        const r = await fetch(`${BASE}/place/${slug}/news?limit=6`);
        if (r.ok) {
          const d = await r.json();
          if (alive) setNews(d.items || []);
        }
      } catch { /* noop */ }
      if (alive) setNewsLoading(false);
    })();

    (async () => {
      try {
        const r = await fetch(`${BASE}/place/${slug}/tts/status`);
        if (r.ok && alive) setTtsStatus(await r.json());
      } catch { /* noop */ }
    })();

    (async () => {
      try {
        const r = await fetch(`${BASE}/place/${slug}/photos?limit=8`);
        if (r.ok) {
          const d = await r.json();
          if (alive) setPhotos(d.items || []);
        }
      } catch { /* noop */ }
    })();

    (async () => {
      try {
        const r = await fetch(`${BASE}/place/${slug}/weather`);
        if (r.ok && alive) setWeather(await r.json());
      } catch { /* noop */ }
    })();

    (async () => {
      try {
        const r = await fetch(`${BASE}/place/${slug}/related?limit=10`);
        if (r.ok) {
          const d = await r.json();
          if (alive) setRelated(d.siblings || []);
        }
      } catch { /* noop */ }
    })();

    return () => { alive = false; };
  }, [slug]);

  const handleListen = async () => {
    if (!slug) return;
    setTtsBusy(true);
    try {
      const r = await fetch(`${BASE}/place/${slug}/tts/generate`, { method: "POST" });
      if (r.ok) {
        const d = await r.json();
        setTtsStatus(d);
        // Play immediately
        const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${d.mp3_url}`);
        audio.play().catch(() => { /* user gesture failed */ });
      }
    } catch { /* noop */ }
    setTtsBusy(false);
  };

  const handleTranslate = async (target) => {
    setLang(target);
    if (target === "en" || !intro) {
      setTranslated(null);
      return;
    }
    setTranslating(true);
    try {
      const r = await fetch(`${BASE}/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: intro.body_html || "",
          target_language: target,
          source_language: "en",
        }),
      });
      if (r.ok) {
        const d = await r.json();
        setTranslated(d.translated_text || d.text || null);
      }
    } catch { /* noop */ }
    setTranslating(false);
  };

  if (!slug) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "#FAFAF7" }}>
        <div className="text-slate-500">No place selected.</div>
      </div>
    );
  }

  const displayHtml = translated || intro?.body_html || "";
  const parentIsCountry = slug.split("/").length === 1;

  return (
    <div className="min-h-screen" style={{ background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }} data-testid="blog-place-hub">
      <BlogHeader activeNav="explore-places" />

      <main className="max-w-[1100px] mx-auto px-6 md:px-10 pt-8 pb-16">
        {/* Photo hero strip — 1 large + up to 3 stacked thumbs */}
        {photos.length > 0 && (
          <section className="mb-8 rounded-2xl overflow-hidden bg-white border" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid="place-photos">
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-0.5">
              <a
                href={photos[0].foreign_url || photos[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="block relative aspect-[16/9] md:aspect-auto md:h-[340px] bg-slate-100 group overflow-hidden"
                data-testid="place-photo-main"
              >
                <img
                  src={photos[0].thumb_url || photos[0].url}
                  alt={photos[0].title || "Place photo"}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 text-white text-[11px]">
                  <span className="inline-flex items-center gap-1"><Camera className="w-3 h-3" /> {photos[0].creator || "Community"} · {photos[0].license || photos[0].source}</span>
                </div>
              </a>
              <div className="grid grid-rows-3 gap-0.5 h-[340px]">
                {photos.slice(1, 4).map((p, i) => (
                  <a
                    key={i}
                    href={p.foreign_url || p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-slate-100 overflow-hidden group"
                    data-testid={`place-photo-thumb-${i}`}
                  >
                    <img
                      src={p.thumb_url || p.url}
                      alt={p.title || ""}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Breadcrumb */}
        <nav className="text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-1.5 flex-wrap" data-testid="place-breadcrumb">
          <button onClick={() => navigate("/blog/places")} className="hover:text-slate-950 font-semibold">Places</button>
          {breadcrumb.map((b, i) => (
            <span key={b.slug} className="inline-flex items-center gap-1.5">
              <ChevronRight className="w-3 h-3 text-slate-400" />
              <button
                onClick={() => navigate(`/blog/places/${b.slug}`)}
                className={`hover:text-slate-950 ${i === breadcrumb.length - 1 ? "text-slate-950 font-semibold" : ""}`}
              >
                {b.label}
              </button>
            </span>
          ))}
        </nav>

        {/* Hero */}
        <header className="mb-8" data-testid="place-hero">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold" style={{ color: "#0F766E" }}>
            <MapPin className="w-3.5 h-3.5" />
            {(intro?.kind || "place").toUpperCase()}
            {intro?.country && intro.kind !== "country" && (
              <span className="text-slate-400 normal-case tracking-normal font-normal">· {intro.country}</span>
            )}
          </div>
          <h1
            className="mt-2 text-slate-950"
            style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: "clamp(2rem, 4vw, 3.4rem)", lineHeight: 1.05, letterSpacing: "-0.02em" }}
          >
            {intro?.name || breadcrumb[breadcrumb.length - 1]?.label || slug}
          </h1>

          {/* Action strip: Listen + Translate + Weather */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={handleListen}
              disabled={ttsBusy}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
              style={{ background: "#0F766E" }}
              data-testid="place-listen-btn"
            >
              {ttsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
              {ttsStatus?.ready ? "Listen ›" : "Listen to intro ›"}
              {ttsStatus?.duration_s ? (
                <span className="text-[11px] opacity-80">{Math.round(ttsStatus.duration_s / 60)} min</span>
              ) : null}
            </button>

            <div className="inline-flex items-center gap-1.5 rounded-full bg-white border px-3 py-1.5 text-sm" style={{ borderColor: "rgba(15,23,42,0.08)" }}>
              <Languages className="w-4 h-4" style={{ color: "#0F766E" }} />
              <select
                value={lang}
                onChange={(e) => handleTranslate(e.target.value)}
                className="bg-transparent outline-none text-sm"
                data-testid="place-lang-select"
              >
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
              {translating && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            </div>

            {weather?.available && (() => {
              const Icon = WEATHER_ICON[weather.icon] || Cloud;
              return (
                <div className="inline-flex items-center gap-2 rounded-full bg-white border px-3.5 py-1.5 text-sm" style={{ borderColor: "rgba(15,23,42,0.08)" }} data-testid="place-weather-chip">
                  <Icon className="w-4 h-4 text-amber-500" />
                  <span className="font-semibold text-slate-900">{Math.round(weather.temperature)}{weather.unit}</span>
                  <span className="text-slate-500 text-xs">{weather.label}</span>
                  {typeof weather.wind_kmh === "number" && (
                    <span className="inline-flex items-center gap-0.5 text-xs text-slate-400">
                      <Wind className="w-3 h-3" /> {Math.round(weather.wind_kmh)} km/h
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        </header>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-10">
          {/* Left: intro */}
          <article data-testid="place-intro-article">
            {introLoading ? (
              <div className="py-16 grid place-items-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <div
                className="prose prose-slate max-w-none prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-3 prose-p:leading-relaxed"
                style={{ fontFamily: "'Lora', serif", fontSize: "1.08rem", color: "#0F172A" }}
                dangerouslySetInnerHTML={{ __html: displayHtml }}
              />
            )}
            {intro?.generating && (
              <div className="mt-6 inline-flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid="place-generating">
                <Loader2 className="w-3 h-3 animate-spin" />
                A richer profile is being generated — refresh in a moment.
              </div>
            )}

            {/* Articles from this place */}
            {articles.length > 0 && (
              <section className="mt-12" data-testid="place-articles">
                <h2 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-4 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" style={{ color: "#0F766E" }} />
                  Stories from {intro?.name || "here"}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {articles.slice(0, 6).map((a) => (
                    <a
                      key={a.id || a.slug}
                      href={`/blog/${a.slug}`}
                      className="block bg-white rounded-2xl p-4 border hover:shadow-md transition-shadow"
                      style={{ borderColor: "rgba(15,23,42,0.06)" }}
                      data-testid={`place-article-${a.slug}`}
                    >
                      <h3 className="text-lg leading-snug line-clamp-2" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>
                        {a.title}
                      </h3>
                      {a.excerpt && <p className="mt-2 text-sm text-slate-600 line-clamp-2">{a.excerpt}</p>}
                      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "#0F766E" }}>
                        Read <ArrowRight className="w-3 h-3" />
                      </span>
                    </a>
                  ))}
                </div>
              </section>
            )}
          </article>

          {/* Right: news + links */}
          <aside className="space-y-6">
            <section className="rounded-2xl border bg-white p-5" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid="place-news">
              <h2 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-4 flex items-center gap-2">
                <Newspaper className="w-3.5 h-3.5" style={{ color: "#0F766E" }} />
                Latest news
              </h2>
              {newsLoading ? (
                <div className="py-6 grid place-items-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
              ) : news.length === 0 ? (
                <div className="text-sm text-slate-500">No recent news found for this place.</div>
              ) : (
                <ul className="space-y-4">
                  {news.map((n, i) => (
                    <li key={i} data-testid={`place-news-item-${i}`}>
                      <a href={n.url} target="_blank" rel="noopener noreferrer" className="group block">
                        <p className="text-sm font-semibold leading-snug text-slate-900 group-hover:underline line-clamp-3">
                          {n.title}
                        </p>
                        <div className="mt-1 flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500">
                          <span className="font-semibold">{n.source || "News"}</span>
                          {n.published && <span className="text-slate-400">· {n.published.slice(5, 16)}</span>}
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {intro?.links && intro.links.length > 0 && (
              <section className="rounded-2xl border bg-white p-5" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid="place-links">
                <h2 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-4 flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" style={{ color: "#0F766E" }} />
                  Reference links
                </h2>
                <ul className="space-y-2">
                  {intro.links.slice(0, 6).map((l, i) => (
                    <li key={i}>
                      <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-700 hover:text-teal-700 hover:underline inline-flex items-start gap-1">
                        <ArrowRight className="w-3 h-3 mt-1 shrink-0" style={{ color: "#0F766E" }} />
                        <span>{l.caption || l.url}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {related.length > 0 && (
              <section className="rounded-2xl border bg-white p-5" style={{ borderColor: "rgba(15,23,42,0.06)" }} data-testid="place-related">
                <h2 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-4 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" style={{ color: "#0F766E" }} />
                  {parentIsCountry ? "Sister countries" : "Nearby places"}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {related.slice(0, 12).map((r) => (
                    <button
                      key={r.slug}
                      onClick={() => navigate(`/blog/places/${r.slug}`)}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-50 hover:bg-teal-50 border px-3 py-1.5 text-sm text-slate-700 hover:text-teal-700 transition-colors"
                      style={{ borderColor: "rgba(15,23,42,0.06)" }}
                      data-testid={`place-related-${r.slug.replace(/\//g, "-")}`}
                    >
                      {r.name}
                      <span className="text-[10px] uppercase tracking-widest text-slate-400 group-hover:text-teal-500">{r.kind}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
